/* eslint-disable no-unused-expressions */
import { observe, observable, action, computed, when } from 'mobx';
import { subscriptionStatusObservable } from 'core/client';

import { analytics } from 'core/analytics';
import { clientStore } from 'apps/client/app';
import { routerStore } from 'core/routing';

import { CadFile } from 'libs/rendering/gltf/api/cadFile';
import { Cad3dViewer } from 'libs/rendering/gltf/api/cad3dViewer';
import {
  ConversionProblemError,
  LogFailError,
  GltfViewerException,
} from 'libs/rendering/gltf/api/errors';
import {
  MeshAnalysisManager,
  DfmCutoffAnalysis,
  DfmStatus,
} from 'libs/rendering/meshAnalysis/appManager';
import { DfmProcessCutoffs } from 'apps/rfq/dfmConfig';

import PropTypes from 'prop-types/prop-types';

import { GltfConnection } from './api/connection';

import { UiModes, UiPosition } from './config';

const uniqueID = (() => {
  let id = 0;
  return () => {
    id += 1;
    return id;
  };
})();

const propsSchema = { isPreview: PropTypes.bool };

export class ViewerControl {
  // wasm module
  scriptPathLocator = null;
  @observable.ref wftyModule = null;
  // Cad3dViewer
  @observable.ref viewer = null;
  @observable canvasSet = false;
  @observable canvasChanged = false;
  // connection
  @observable offline = false;
  disposeFileReady = null;
  disposeDiffFileReady = null;
  @observable gltfConnections = new Map();
  @observable downloadPerc = 0.0;
  // - the FileApp that we queried from the graphql api
  @observable file = null;
  @observable diffFile = null;
  // - the CadFile is the wasm representation of the FileApp once
  // it is loaded into the viewer, its easy to imagine a sceneario
  // where, while loading, the cadFile and file refer to different
  // files
  @observable.ref cadFile = null;
  @observable.ref diffCadFile = null;
  // - conversionProblem is meant to indicate a conversionProblem beyond
  // a failed conversion, if there is an unforseen problem in the viewer
  // when parsing a successful conversion, then this flag should be set
  @observable conversionProblem = false;
  // - viewerFailed is set when LogFail is called, it will cause the viewer
  // to stop rendering, and display an error message, it is considered
  // recoverable and display a Reload button to the user
  // - if the tab crashes we can't do anything about it
  @observable viewerFailed = false;
  // - the viewer is kept alive in the background, so flags
  // like viewerReady, cadReady will be true as long as a
  // valid file has been loaded before, so we need another
  // flag from which we can tell if the viewer is currently
  // visible or hidden, this is the first one being set
  // and only indicates if the viewer is visible or not
  // - it is different from the other flags because this is set
  // to false in Unmount, so could also be called viewerMounted
  @observable viewerVisible = false;
  // - this is set in startRendering, so once this is set we're rendering
  // and can execute opengl, but the CadFile is still loading
  @observable viewerReady = false;
  // TODO: I think I accidently created viewerReady but eventsReady is the
  // same thing
  @observable eventsReady = false;
  // - this is set before cadReady, it indicates we loaded the file and
  // the assembly tree(?) is ready (UserActionCadAssemblyReady)
  @observable assemblyReady = false;
  // - cadReady is set to true once the entire file has been loaded the
  // viewer is rendering, once this flag is set the viewer should be
  // completely ready (UserActionCadRequestFinished)
  @observable cadReady = false;
  // UI
  // LightModal
  @observable _lightModalStart = false;
  @observable lightModalVisible = false;
  @observable lightModalContentIndex = 0;
  // Ui "context" (assembly, geometry...)
  @observable uiMode = UiModes.collapsed;
  // left panel resize (constant by now)
  @observable leftPanelWidth = UiPosition.panel;
  // right click menu
  @observable contextMenuOpened = false;
  contextMenuItem = null;
  // annotations
  @observable toolBarExpanded = null;
  @observable expandedPinKey = null;
  @observable hoveredPinKey = null;
  // HACK: I needed this because the isPreview flag is now
  // inside the wasm, but I can't access that while the viewer
  // is initializing
  // TODO: make the c++ use the viewerControl isPreview so
  // we can make this one the one and only isPreview flag
  @observable viewerControlIsPreview = false;
  // lastLoadedId is so we can compare the file.id we are about
  // to load to the lastLoadedId, if they are the same we can
  // just unhide the viewer and don't need to load the file
  @observable lastLoadedId = undefined;

  @observable.ref meshAnalysis = null;
  @observable debugFeatures = false;
  @observable activePanelType = null;

  reportedExceptions = undefined;

  constructor(contextManager = null, isPreview = false) {
    const id = uniqueID();
    this.wrapperId = `gltfWrapper${id}`;
    this.canvasId = `gltfCanvas${id}`;
    this.obsMap = new Map();
    this.reportedExceptions = new Map();
    this.meshAnalysis = new MeshAnalysisManager(
      null,
      {},
      { autoDispose: false },
    );

    // connection/subscription
    this.offline = false;
    this.disposers = [];

    // observe the client status
    const subscriptionStatusDisposer = observe(
      subscriptionStatusObservable,
      change => {
        // check if the client is offline
        this.offline = change.newValue === 'disconnected';
      },
    );
    this.disposers.push(subscriptionStatusDisposer);
    this.disposers.push(() => {
      this.meshAnalysis.dispose();
    });

    // - scene-ready promise (for previews)
    // - there is another readyPromise that emscripten creates, but we did
    // our own thing here, sceneReadyPromise is resolved in
    // cad3dViewer.handleUserEvent by calling this.___sceneReadyPromiseResolve
    this.sceneReadyPromise = new Promise(resolve => {
      this.___sceneReadyPromiseResolve = resolve;
    });

    // pass this to contextManger...
    if (contextManager) {
      contextManager.SetViewerControl(this);
    }

    this.viewerControlIsPreview = isPreview;
  }

  // internal
  async loadWasm() {
    if (this.wftyModule) return;

    // load emscriptem module
    const gltfModuleURL =
      MODE === 'development'
        ? `${STATIC_URL_BASE}/assets/gltf/gltf_web_viewer.js?deploy=${WASM_DEPLOY}`
        : `${STATIC_WASM_URL}/latest/gltf_web_viewer.js?deploy=${WASM_DEPLOY}`;

    const emscriptenModule = await import(
      /* webpackIgnore: true */ gltfModuleURL
    );
    if (!emscriptenModule) {
      this.logWarn('cannot download emscripten module', gltfModuleURL);
      return;
    }
    const loadWftyModule = emscriptenModule.default;

    // load wasm module
    const [gltfWasm, gltfData] = [
      MODE === 'development'
        ? `${STATIC_URL_BASE}/assets/gltf/gltf_web_viewer.wasm?deploy=${WASM_DEPLOY}`
        : `${STATIC_WASM_URL}/latest/gltf_web_viewer.wasm?deploy=${WASM_DEPLOY}`,
      MODE === 'development'
        ? `${STATIC_URL_BASE}/assets/gltf/gltf_web_viewer.data?deploy=${WASM_DEPLOY}`
        : `${STATIC_WASM_URL}/latest/gltf_web_viewer.data?deploy=${WASM_DEPLOY}`,
    ];
    this.scriptPathLocator = {
      locateFile(path) {
        if (path.endsWith('.wasm')) {
          return gltfWasm;
        }
        return gltfData;
      },
    };

    // - this is not used anymore, to enable it use the
    // ABORT_ON_WASM_EXCEPTIONS=1 flag
    this.scriptPathLocator.onAbort = what => {
      const scopingFunction = scope => {
        scope.setTag('subsystem', 'wasm-unhandled-exception');
        scope.setTag('exception', what);
      };
      this.LogException(what, scopingFunction);
    };

    this.wftyModule = await loadWftyModule(this.scriptPathLocator);
  }

  async loadCadViewer(props) {
    // load wasm module if not loaded yet
    await this.loadWasm();

    // new Cad3dViewer, so if there're wrapped wasm objects, forget about them
    this.deleteWasmObservables();

    if (!this.viewer) {
      this.viewer = new Cad3dViewer(this.wftyModule, this, this.isDiffRoute);
      const version = this.wasmVersion?.Describe();
      this.logInfo(`viewer version: ${version}`);
    }

    // set defaults
    if (props) {
      try {
        PropTypes.checkPropTypes(propsSchema, props);
        if (props.isPreview) {
          this.viewer.isPreview = props.isPreview;
          this.logInfo(`viewer isPreview: ${this.viewer.isPreview}`);
        }
        // TODO: perspective/ortho, faces/wireframe, upAxis... etc
      } catch (ex) {
        this.logWarn(`cannot apply viewer props: ${ex.toString()}`);
      }
    }
  }

  async loadCadFile(fileApp, props = null) {
    if (!fileApp) {
      this.logWarn('cannot load null FileApp');
      return;
    }

    this.disposeFileReady && this.disposeFileReady();

    // load the viewer if not loaded yet
    await this.loadCadViewer(props);

    const viewId = this?.lastLoadedId;

    const newFileId = fileApp?.id;
    if (newFileId && (!this.cadFile || viewId !== newFileId)) {
      // file changed,
      this.disposeFileReady = when(
        () =>
          this.file &&
          this.file.conversionGltfjson?.status === 'SUCCESS' &&
          !this.gltfConnections.has(this.file.id) &&
          this.viewer,
        () => {
          // initialize the connection, this should start downloading the gltf and buffers
          this.gltfConnections.set(
            this.file.id,
            new GltfConnection(this, this.file.id),
          );

          // null CadFile can be reset to its initial state
          this.viewer.SetCadFile(null);

          // set or update default canvas
          this.SetCanvas();

          // set the new cad fileset
          const cadFile = new CadFile(this, newFileId);
          this.viewer.SetCadFile(cadFile);
          this.cadFile = cadFile;
          // new cadFile, so if there're wrapped wasm objects, forget about them
          this.deleteWasmObservables();

          if (this.wftyModule?.canvas) {
            // - this is where we start rendering something to the canvas, so
            // if we're here, we've loaded a file and everything is fine so we
            // can start rendering
            // - keep in mind we're async here
            this.viewer.updateCanvas();
            this.viewer.clearCanvas();
            this.startRendering();

            this.lastLoadedId = fileApp.id;
          }
        },
      );
    }
  }

  async loadDiffCadFile(fileApp) {
    if (!fileApp) return;

    this.disposeDiffFileReady && this.disposeDiffFileReady();

    if (this.gltfConnections.size > 10) {
      // - this is a safety mechanism to prevent loading too many files into the viewer
      // and use up all ram: delete all loaded diff files after we loaded at least 10 files
      this.diffFile = null;
      this.diffCadFile = null;
      this.viewer.SetDiffCadFile(null);
      const eraseIds = [];
      this.gltfConnections.forEach((connection, id) => {
        if (connection.fileId !== fileApp.id) {
          // - need to only delete the gltfConnections for cadDifffiles, not the one for the
          // main cadFile
          eraseIds.push(id);
        }
      });
      for (let i = 0; i < eraseIds.length; i++) {
        this.gltfConnections.delete(eraseIds[i]);
      }
    }

    const newFileId = fileApp?.id;
    this.diffFile = fileApp;

    if (newFileId && !this.gltfConnections.has(newFileId)) {
      // initialize the connection, and start downloading the gltf and buffers
      this.disposeDiffFileReady = when(
        () =>
          this.diffFile &&
          this.diffFile.conversionGltfjson?.status === 'SUCCESS',
        () => {
          if (!this.gltfConnections.has(newFileId)) {
            this.gltfConnections.set(
              newFileId,
              new GltfConnection(this, newFileId),
            );
            const cadFile = new CadFile(this, newFileId);

            this.viewer.SetDiffCadFile(cadFile);
            this.diffCadFile = cadFile;
          }
        },
      );
    } else if (newFileId) {
      this.viewer.RestoreDiffCadFile(newFileId);
    }

    this.viewer.startDiffing();
  }

  unloadCadFile = () => {
    this.cadFile?.destroy();
    this.cadFile = null;
    this.cadReady = false;
    this.assemblyReady = false;

    this.file = null;
    this.lastLoadedId = null;

    this.unloadDiff();

    this.clearConnection();

    // TODO: delete wasm objects?
  };

  unloadDiff = () => {
    this.diffFile = null;
    this.diffCadFile = null;

    this.viewer?.SetDiffCadFile(null);

    // TODO: delete wasm objects?

    // clean URL
    const routeParams = routerStore.route.params;
    const newParams = { ...routeParams };
    if (newParams.panel === 'diff') delete newParams.panel;
  };

  // TODO: the lifetime of the wasm observables could be controlled in c++,
  // try making destructors in all the relevant c++ classes which then call
  // some js code to remove their wasm observables
  // - it is also called in Unload()
  deleteWasmObservables = () => {
    // delete wasm observables...
    this.obsMap.clear();
    this.viewer?.clear();
  };

  clearConnection = () => {
    this.offline = false;
    this.disposeFileReady && this.disposeFileReady();
    this.disposeDiffFileReady && this.disposeDiffFileReady();
    this.gltfConnections.clear();
    this.downloadPerc = 0.0;
  };

  startRendering = () => {
    if (this.viewer) {
      this.viewer.startRendering();
      this.viewerReady = true;
      this.viewerVisible = true;
    }
  };

  @computed get isPreview() {
    return this.viewerControlIsPreview || this.viewer?.isPreview;
  }

  @computed get wasmVersion() {
    if (this.wftyModule) {
      return new this.wftyModule.VersionEmbind();
    }
    return null;
  }

  loseContext = () => {
    const { ctx } = this.wftyModule;
    if (ctx) {
      ctx.getExtension('WEBGL_lose_context').loseContext();
    }
  };

  @computed get isSinglePartFile() {
    if (this.conversionProblem || this.viewerFailed) {
      return undefined;
    }
    if (this.viewerVisible && this.assemblyReady) {
      return this.cadFile?.assembly?.InstanceCount() === 1;
    }
    return undefined;
  }

  // panel type can reference `CADPanelType`
  @action SetActivePanel = (type, autoToggle = true) => {
    this.uiMode = UiModes.collapsed;

    if (type === this.activePanelType && autoToggle) {
      // close current panel
      this.activePanelType = null;
      return;
    }

    this.activePanelType = type;
  };

  @action SetCanvas = (newWrapperId = null, newCanvasId = null) => {
    if (!document) {
      this.LogException('cannot set viewer canvas, null document');
      return;
    }

    // set new IDs
    if (newWrapperId) {
      this.wrapperId = newWrapperId;
    }
    if (newCanvasId) {
      this.canvasId = newCanvasId;
    }

    // apply IDs
    const viewerHolder = document.getElementById(this.wrapperId);
    const canvasHolder = document.getElementById(this.canvasId);
    if (
      viewerHolder &&
      canvasHolder &&
      viewerHolder.tagName.toLowerCase() === 'div' &&
      canvasHolder.tagName.toLowerCase() === 'canvas' &&
      viewerHolder.contains(canvasHolder)
    ) {
      this.wftyModule.canvas = canvasHolder;

      // create proper WebGL context for the viewer
      this.wftyModule.createContext(canvasHolder, true, true, {
        antialias: true, // this fixes jaggy lines (if client browser + GPU manges antialiasing in webGL...)
        premultipliedAlpha: false, // this fixes odd WebGL transparency behaviour
      });

      this.canvasSet = this.viewer?.setCanvas(viewerHolder, canvasHolder);
      if (this.canvasSet) {
        this.canvasChanged = true;

        // add canvas event listeners
        viewerHolder.addEventListener(
          'wheel',
          e => {
            e.preventDefault();
          },
          {
            passive: false,
          },
        );

        // prevent the default behaviour (so it recovers from a lost context)
        canvasHolder.addEventListener(
          'webglcontextlost',
          e => {
            e.preventDefault();
            if (!this.viewer) {
              return;
            }
            canvasHolder.style.visibility = 'hidden';
            if (this.viewer) {
              this.viewerReady = false;
              this.cadReady = false;
              this.viewer.stop();
            }
          },
          false,
        );

        canvasHolder.addEventListener(
          'webglcontextrestored',
          () => {
            if (!this.viewer) {
              return;
            }
            // re-create the viewer...
            try {
              if (this.viewer) {
                const reloadStatus = this.viewer.reload();
                if (reloadStatus === 0) {
                  // resume rendering
                  this.viewer.settings.reportWasmChange();
                  this.cadReady = true;
                  this.SyncSelectionMode();

                  // setting the style.visibility like so works around the fact that
                  // viewerVisible is used to indicate if the Viewer is mounted/unmounted,
                  // which is different from contextlost/restored that we're handling here,
                  // so thats why I left this here instead of using viewerVisible because
                  // I didn't want the viewerVisible to be triggered when the Viewer is
                  // not mounted
                  canvasHolder.style.visibility = 'visible';
                  if (this.viewerVisible) {
                    this.startRendering();
                  }
                } else {
                  // cannot recreate state, reload the page...
                  this.LogException('cannot reload the viewer, refreshing');
                  window.location.reload();
                }
              }
            } catch (ex) {
              this.LogException('cannot recreate webGL context, refreshing');
              // cannot recreate state, reload the page...
              window.location.reload();
            }
          },
          false,
        );
      } else {
        this.LogException('cannot set viewer canvas');
      }
    } else {
      this.LogException(
        `cannot find HTML elements by id ${this.wrapperId} ${this.canvasId}`,
      );
    }
  };

  @action completelyUnload = () => {
    // unload canvas
    this.viewer?.setCanvas(null, null);
    this.canvasSet = false;
    this.canvasChanged = false;
    if (this.wftyModule) {
      this.wftyModule.canvas = null;
    }

    this.clearConnection();

    // unload file
    this.conversionProblem = false;
    this.unloadCadFile();

    this.deleteWasmObservables();

    // UI defaults
    this._userStart = false;
    this.uiMode = UiModes.collapsed; // Ui "context" (assembly, geometry...)
    this.lightModalVisible = false;
    this.lightModalContentIndex = 0;
    this.leftPanelWidth = UiPosition.panel; // left panel resize width size
    this.contextMenuOpened = false;
    this.toolBarExpanded = null;
    this.expandedPinKey = null;
    this.hoveredPinKey = null;

    // destroy the viewer
    this.viewer?.destroy();
    this.viewer = null;
    this.viewerVisible = false;
    this.viewerReady = false;

    this.logWarn('viewer destroyed');
  };

  @action partialUnload = () => {
    // unload file
    this.conversionProblem = false;
    this.unloadCadFile();

    this.deleteWasmObservables();

    // UI defaults
    this._userStart = false;
    this.uiMode = UiModes.collapsed; // Ui "context" (assembly, geometry...)
    this.lightModalVisible = false;
    this.lightModalContentIndex = 0;
    this.leftPanelWidth = UiPosition.panel; // left panel resize width size
    this.contextMenuOpened = false;
    this.toolBarExpanded = null;
    this.expandedPinKey = null;
    this.hoveredPinKey = null;

    // destroy the viewer
    // could be avoided, but needed right now
    this.viewer?.destroy();
    this.viewer = null;
    this.viewerVisible = false;
    this.viewerReady = false;
  };

  @action Unmount = () => {
    const heapSize = this.heap8SizeMb().toFixed(2);
    this.logWarn(`unmounting viewer, heapSize: ${heapSize}Mb`);

    this.viewerVisible = false;
    // - I set this to false so that when we use the browser history to navigate
    // forward to the viewer, then the useEffect for in viewer.js for syncing the
    // url prodId/partId params runs again, the effect is that we inspect the part
    // we were previously inspecting when we go back to the viewer using browser
    // history, otherwise we always end up on the main assembly
    this.eventsReady = false;

    const { size } = this.reportedExceptions;
    if (size > 0) {
      this.logInfo(`clearing out ${size} reported exceptions:`);
      this.reportedExceptions.forEach((_, k) => {
        this.logInfo(`clearing: ${k}`);
      });
      this.reportedExceptions.clear();
    }

    // - when this.conversionProblem is true, then we have
    // a problem with a conversion, but the viewer is fine
    // - when this.viewerFailed is true, then there was
    // some exception in the wasm and we probably need to completely
    // unload the viewer
    // - to prevent to keep showing the error message, we need to
    // reset the viewerFailed = false, but also reset the
    // file to null to force the useEffect in canvas3D to run,
    // should we try to go back to the same file without reloading
    // - I also added lastLoadedId = null because I added startRendering
    // to the useEffect that sets viewerVisible = true when the file
    // changes, but thought that if the file previously error'ed,
    // then we should not call startRendering, and setting lastLoadedId
    // = null should prevent that
    if (this.conversionProblem || this.viewerFailed) {
      this.conversionProblem = false;

      this.lastLoadedId = null;
      this.file = null;
      this.assemblyReady = false;
    }

    if (this.viewerFailed) {
      this.deleteWasmObservables();
      this.viewer?.destroy();
      this.viewer = null;

      this.viewerFailed = false;
    } else if (this.viewer) {
      // - stop just means "stop rendering", the functions will still
      // work after that
      this.viewer.stop();

      // - set selection mode to default, this should disable any measuring
      const { selectionManager } = this.viewer;
      const camera = this.viewer.settings.CurrentCamera();
      if (camera) {
        const defaultSelMode = camera.GetDefaultSelMode(this.viewer.mode);
        selectionManager.SetSelectionMode(defaultSelMode);
      }

      // - if I don't clear the annotations from the notePad here, then
      // when the viewer is switched between two files that are the same
      // (don't even have to be copies?), then the annotations from one
      // can show up on the other one
      this.viewer.notePad.ClearAnnotations();

      // - turn off annotation mode
      this.DisableAnnotationMode();

      // - need to set explode mode to false, otherwise we can go
      // back to the viewer and load a single part file, and have
      // explode mode active in a single part file with no way
      // to turn it off
      this.ToggleExplodeMode(false);

      // - close the context menu
      this.CloseContextMenu();
      // - reset download state so it starts at 0% next time we
      // load something
      this.downloadPerc = 0;

      // - only preview viewers really unload when leaving the page
      if (this.isPreview) {
        this.completelyUnload();
      } else {
        this.partialUnload();
      }
    }

    this.meshAnalysis.UnsubscribeMeshAnalysis();
  };

  @action Mount = (file, status, contextManager) => {
    if (status !== 'SUCCESS' && this.lastLoadedId === file?.id) {
      this.completelyUnload();
    }

    if (this.lastLoadedId === file?.id) {
      // reuse the already running viewer
      // NOTE: currently, this never happens as we always destroy the viewer when unmounting
      this.viewer.navCube.visible = true;
      this.startRendering();
    } else {
      if (!this.file) {
        // we need to load the cadFile before mounting
        this.loadCadFile(file);
      }

      // - disable section planes only when loading a new file
      this.DisableSectionMode(false);
      // load a new file:
      // - we don't know if loading will be successful, and it is async
      // - cadReady and viewerReady are flags that are set once the the CadFile
      // is ready, and once the viewer is ready, so before triggering loading
      // a new CadFile we need to reset them here so all logic that relies on
      // them knows the viewer is "not ready" while loading
      this.cadReady = false;
      this.assemblyReady = false;
      this.viewerReady = false;

      this.file = file;
      if (this.file?.id && !this.isPreview) {
        this.contextManager = contextManager;
        // this.meshAnalysis.SubscribeMeshAnalysis(
        // file.id,
        // contextManager?.context?.id,
        // );
      }
    }
  };

  @action RequestSignIn = () => {
    if (this.gltfConnections.size > 0 && this.downloadPerc < 0.99) {
      // connection failed before completion, cancel it
      this.clearConnection();
    }

    if (clientStore?.routerStore) {
      // redirect to sign-in with next param, so
      //  it gets back to the viewer right away
      clientStore.routerStore.navigate('home.signIn', {
        next: clientStore.next(),
      });
    }
  };

  /*
    Async Initialization
  */

  // viewer wasm callback
  @action SetAssemblyReady(value) {
    if (this.assemblyReady !== value) {
      this.assemblyReady = value;
    }
  }

  /*
    Viewer render
  */

  isContextLost() {
    const canvasHolder = document.getElementById(this.canvasId);
    if (!canvasHolder) return false;
    let gl = canvasHolder.getContext('webgl2');
    if (gl == null) {
      gl = canvasHolder.getContext('webgl1');
      if (gl == null) {
        gl = canvasHolder.getContext('webgl');
        if (gl == null) {
          return false;
        }
      }
    }
    return gl.isContextLost();
  }

  @action ForceRender = () => {
    this.cadFile?.assembly?.TriggerStateChange();
  };

  /*
    UI mode
  */

  @action UpdateUiMode = mode => {
    this.activePanelType = null;
    if (this.uiMode !== mode) {
      this.uiMode = mode;
      // expand/update left menu
      if (mode === UiModes.assembly) {
        // expanding the assembly browser, so expand root node
        const root = this.cadFile.assembly.GetProduct(0);
        if (root !== null) {
          if (!root.expanded) {
            root.expanded = true;
            const subRoot = root.GetChild(0);
            if (subRoot && !subRoot.IsSubassembly()) {
              subRoot.expanded = true;
            }
          }
        }
      }
    } else {
      // collapse left menu
      this.uiMode = UiModes.collapsed;
    }

    // viewer (wasm) uimode must be updated too...
    if (this.viewer) {
      this.viewer.mode = this.uiMode;
    }

    const selModeEnum = this.viewer?.module.SelectionMode;
    const selMode = this.viewer?.selectionManager?.GetSelectionMode();
    const keepSelMode = selMode === selModeEnum.SelectionModeMeasurements;
    if (keepSelMode) return;

    // selection mode changes depends on current left menu
    this.SyncSelectionMode();
  };

  // uiMode <--> selection mode sync
  @action SyncSelectionMode = () => {
    const selManager = this.viewer?.selectionManager;
    if (!selManager) return;

    const camera = this.viewer?.settings?.CurrentCamera();
    if (!camera) return;

    const defaultSelMode = camera.GetDefaultSelMode(this.viewer.mode);
    selManager.SetSelectionMode(defaultSelMode);
  };

  /*
    Selection manager
  */

  @action SetHighlightBehaviour = (mode, item) => {
    const { engine } = this.viewer;
    if (mode === 'on-front') {
      if (item == null) {
        // all on-front
        engine.selectionOnFront = true;
      } else {
        // just an item on-front
        engine.SetShowOnFront(item._wasm);
      }
    } else {
      // nothing on-front
      engine.selectionOnFront = false;
      engine.SetShowOnFront(null);
    }
  };

  // auto-scroll timer utils
  @action SetScrollTimer = newTimer => {
    this.scrollTimer = newTimer;
  };

  /*
    Pin Notes
  */

  pinTargetType = noteId => {
    const note = this.viewer?.notePad?.GetNote(noteId);
    const target = note?.GetLink().GetTarget();
    return target ? (target.IsInstance() ? 'part' : 'feature') : null;
  };

  pinBelongsToCamera = (note, camera) => {
    if (!note) return false;
    if (!this.viewer) return false;

    if (!camera) return false;
    if (camera.GetIndex() === 0) return true;

    const target = note.GetLink().GetTarget();
    const isTargetDrawing = target.IsTypeDrawing();

    if (camera.IsDrawing()) {
      const drawing = camera.GetDrawing();
      const currSheetIx = drawing ? drawing.currentSheetIx : -1;
      return (
        isTargetDrawing &&
        camera.GetInstanceIndex() === target.instanceHitIx &&
        currSheetIx === target.brepIx
      );
    }

    if (camera.IsInstance()) {
      return (
        !isTargetDrawing && camera.GetInstanceIndex() === target.instanceHitIx
      );
    }

    if (camera.IsSubAssembly()) {
      if (!isTargetDrawing) {
        const { assembly } = this.cadFile;
        const instance = assembly?.GetInstance(target.instanceHitIx);
        const prod = camera.GetSubAssembly();
        if (prod && instance) {
          return prod.IsInstanceBelow(instance._wasm);
        }
      }
      return false;
    }

    return !isTargetDrawing;
  };

  // - highlighting a pin target means showing the geometry
  // primitive that was clicked when the pin was created, so
  // a triangle or face most likely
  @action HighlightPinTarget = keyOrAnnotationId => {
    const note = this.viewer?.notePad?.GetNote(keyOrAnnotationId);
    if (!note) return;
    const target = note.GetLink().GetTarget();

    if (note && note.IsOk()) {
      if (this.viewer.selectionManager) {
        this.UnHighlightEverything();
        this.HighlightTarget(target);
        this.hoveredPinKey = note.key;
      }
    }
  };

  // - this clears all highlighted things
  // - it does both ClearSelection and ClearHighlight, though the
  // first might have to be removed when we use selection for anything
  // meaningful
  // - but currently selection serves no purpose, but markup elements
  // are highlighted by selecting them
  // - so to really unhighlight everything, I have to deselect all
  // markup elements
  // - I could also specifically unselect them with
  // selectionManager.DeleteSelectionItem but I found that to be really
  // unreliable when I tried it, so for now just deselecting everything
  // is a more reliable solution
  @action UnHighlightEverything = () => {
    this.viewer?.selectionManager?.ClearSelection();
    this.viewer?.selectionManager?.ClearHighlight();
    this.hoveredPinKey = null;
  };

  // - as I said above markup elements are highlighted by selecting them,
  // measurements are markup items, so this is called SelectAndHighlight
  // because it does both of these things (it selects the markup for the
  // measurement, and highlights the targets of the measurements endpoints)
  @action SelectAndHighlightMeasurement = keyOrAnnotationId => {
    const measurement = this.viewer?.notePad?.GetMeasurement(keyOrAnnotationId);
    if (!measurement || measurement.isTemp) return;
    this.UnHighlightEverything();

    this.viewer?.selectionManager.AddSelectionItem(measurement._wasm);

    const { startTgt, endTgt } = measurement;

    // - small hack to prevent highlighting a face when the measurement
    // endpoint are both on the same face, because then the measurement
    // and face are highlighted in the same color and it will be hard to
    // read the measurement
    // - there is a ItemTarget::EqualIndices function that should be better
    // here, but it didn't really work as expected
    // - so instead I do this simple thing, I check if we hit the same
    // instance and if we do I check if we hit the same face and if do
    // that too then I just early return
    if (
      startTgt.faceHit &&
      endTgt?.faceHit &&
      startTgt.instanceHitIx === endTgt?.instanceHitIx &&
      startTgt.faceHitIx === endTgt?.faceHitIx
    ) {
      return;
    }

    this.HighlightTarget(startTgt);
    if (endTgt) {
      // - endTgt is optional for !isFromTo measurements
      this.HighlightTarget(endTgt);
    }
  };

  // - generic way to highlight a target used in both HighlightPinTarget
  // and SelectAndHighlightMeasurement
  @action HighlightTarget = target => {
    if (this.viewer.selectionManager) {
      // - I have no idea what the difference between SetIndirectPin and
      // SetIndirect is
      if (!target.IsInstance()) {
        // pin
        target.SetIndirectPin();
        this.viewer.selectionManager.AddHighlight(target._wasm);
      } else {
        // indirect
        target.SetIndirect();
        this.viewer.selectionManager.AddHighlight(target._wasm);
      }
    }
  };

  // - these make sure that we can use either note.key or note.annotationId
  // and check if the associated Pin is expanded (or hovered)
  // - it does that by first getting the note, then using its note.key
  // - this way we make sure we always use note.key in the comparison, and
  // expandedPinKey and hoveredPinKey should always be set to note.key
  isPinExpanded = keyOrAnnotationId => {
    if (!keyOrAnnotationId) {
      return false;
    }
    if (!this.expandedPinKey) {
      return false;
    }
    if (keyOrAnnotationId === this.expandedPinKey) {
      return true;
    }

    const note = this.viewer?.notePad?.GetNote(keyOrAnnotationId);
    if (!note) return false;

    return (
      note.key === this.expandedPinKey ||
      note.annotationId === this.expandedPinKey
    );
  };

  isPinHovered = keyOrAnnotationId => {
    if (!keyOrAnnotationId) {
      return false;
    }
    if (!this.hoveredPinKey) {
      return false;
    }
    if (keyOrAnnotationId === this.hoveredPinKey) {
      return true;
    }

    const note = this.viewer?.notePad?.GetNote(keyOrAnnotationId);
    if (!note) return false;

    return (
      note.key === this.hoveredPinKey ||
      note.annotationId === this.hoveredPinKey
    );
  };

  // - TogglePinExpand opens the dialog for a pin that contains the
  // Note (as in the text) for that Pin
  @action TogglePinExpand = keyOrAnnotationId => {
    let note = null;
    if (!keyOrAnnotationId && this.expandedPinKey !== null) {
      // - when passing null for keyOrAnnotationId this uses the expandedPinKey
      // (when it exists) to close the currently expanded Pin
      note = this.viewer?.notePad?.GetNote(this.expandedPinKey);
    } else if (keyOrAnnotationId) {
      note = this.viewer?.notePad?.GetNote(keyOrAnnotationId);
    }

    if (!note) return;

    if (note.IsOk() && !this.isPinExpanded(keyOrAnnotationId)) {
      // - making note have both key and annotationId is overall better, but
      // for this expandedPinKey thing it sucks
      // - I make sure to use the note.key here, this should always be the right
      // thing to use, and I implemented the functions isPinExpanded and
      // isPinHovered above and used them everywhere we do want to check
      // this expandedPinKey/hoveredPinKey against a note.key or annotationId
      this.expandedPinKey = note.key;

      const currentCamera = this.viewer.settings.CurrentCamera();
      const noteCameraData = note.GetCameraDataJsonObj();
      const forCadId = noteCameraData.cadId;
      if (currentCamera.Is3D() && !currentCamera.IsPlaneXY()) {
        if (
          (forCadId === 0 || forCadId) &&
          forCadId !== currentCamera.GetItem().uuid
        ) {
          // - inspect either part or product, depending on type of id
          // - this is kind of bad that there are different types of cadIds,
          // also it seem a cadId can be of the form prodId-partId, where we
          // want either prodId or partId, therefore InspectById currently
          // tries to work around that by splitting and testing if either
          // of the splitIds finds something to inspect
          // - because of the cadId being of the form prodId-partId I ran
          // into a problem here where we try to find prodId-partId, and then
          // we don't find a part, but we do find a product, but it is some
          // kind of invisible container product for just the part, and if
          // we inspect it the camera ends up looking at nothing, doing the
          // splitId thing works around that issue, but is not a nice solution
          // - TODO: the camera(data) should hold a cadId that works correctly
          // in all cases
          this.InspectById(forCadId);
        } else {
          // - animation to pin note camera
          this.viewer.settings.SetCameraTarget(noteCameraData);
        }
      }
    } else {
      this.expandedPinKey = null;
    }
  };

  // - this moves the camera to focus on the measurement identified
  // by keyOrAnnotationId
  @action CameraToMeasurement = keyOrAnnotationId => {
    const measurement = this.viewer?.notePad?.GetMeasurement(keyOrAnnotationId);
    if (!measurement || measurement.isTemp) return;

    const measurementCameraData = measurement.GetCameraDataJsonObj();
    this.viewer.settings.SetCameraTarget(measurementCameraData);
  };

  /*
    Right-click menu
  */

  @computed get hoveredMarkup() {
    const selManager = this.viewer?.selectionManager;
    if (!selManager) return null;
    if (selManager?.MkpHighlightCount() !== 1) return null;
    const lastHighMkp = selManager.GetLastMarkupHighlight();
    return lastHighMkp;
  }

  @computed get hoveredTarget() {
    const selManager = this.viewer?.selectionManager;
    if (!selManager) return null;

    const selTarget =
      // eslint-disable-next-line no-nested-ternary
      selManager.HighlightCount() === 1 || this.uiMode === UiModes.assembly
        ? selManager.GetLastHighlight()
        : selManager.HighlightCount() > 1
        ? selManager.GetFirstHighlight()
        : null;

    if (selManager.HighlightCount() > 1 && selTarget?.isUi) {
      // multiple different faces from a UI component...
      return null;
    }
    return selTarget;
  }

  @computed get hoveredCadItem() {
    const selTarget = this.hoveredTarget;
    if (!selTarget) return null;

    const selManager = this.viewer?.selectionManager;
    if (!selManager) return null;

    const useDiff =
      this.viewer?.isDiffing &&
      selTarget.cadFileId === this.viewer?.diffTool.diffId;
    const assembly = useDiff
      ? this.diffCadFile?.assembly
      : this.cadFile?.assembly;
    if (!assembly) return null;

    const instance = selTarget
      ? assembly.GetInstance(selTarget.instanceHitIx)
      : null;

    if (!instance) return null;

    if (selTarget.IsInstance()) {
      // INSTANCE
      this.contextMenuItem = instance;
      return instance;
    }

    // faces, edges...
    const body = instance.GetBody();
    if (!body) return null;
    const wasmObj = body?.isFaceted
      ? body.GetBodyMesh()?.GetMeshFrame()
      : body.GetBrep();
    if (!wasmObj || wasmObj.faceCount === 0) return null;

    let face = wasmObj.GetFace(selTarget.faceHitIx);
    if (!face) face = wasmObj.GetFace(selTarget.topoIndex);
    if (face) {
      if (selTarget.IsFace()) {
        // FACE
        this.contextMenuItem = face;
        return face;
      }
      if (selTarget.IsLine()) {
        const loop = face.Loop(selTarget.loopHitIx);
        if (loop) {
          const edge = loop.Edge(selTarget.edgeHitIx);
          if (edge) {
            // EDGE
            this.contextMenuItem = edge;
            return edge;
          }
        }
      }
    }

    return null;
  }

  @action CloseContextMenu = () => {
    this.contextMenuItem = null;
    this.contextMenuOpened = false;
  };

  @action ParseInstancePmi = instance => {
    const brep = instance?.GetBody()?.GetBrep();
    if (!brep || brep.isDataReady) return true;

    const part = instance?.GetBody()?.GetPart();
    if (part) {
      const status = this.cadFile?._wasm.PopulatePartPMI(part._wasm);
      if (status?.error !== 0) {
        console.warn('error parsing instance PMI json', status, instance);
        return false;
      }
      return true;
    }
    return false;
  };

  /*
    Tab Manager
  */
  cadItemByInstanceId = cadId => {
    if ((cadId === 0 || cadId) && this.cadReady) {
      const { assembly } = this.cadFile;
      if (assembly) {
        const instance = assembly.GetInstanceById(cadId);
        if (instance) {
          return instance;
        }
      }
    }
    return null;
  };

  cadItemByPartId = cadId => {
    if ((cadId === 0 || cadId) && this.cadReady) {
      let partId = cadId;
      const splitId = cadId.split('-');
      if (splitId.length > 1) {
        [, partId] = splitId;
      }

      const { assembly } = this.cadFile;
      if (assembly) {
        const instance = assembly.GetInstanceByBodyId(partId);
        if (instance) {
          return instance;
        }
      }
    }
    return null;
  };

  cadItemByProdId = cadId => {
    if ((cadId === 0 || cadId) && this.cadReady) {
      let prodId = cadId;
      const splitId = cadId.split('-');
      if (splitId.length > 1) {
        [prodId] = splitId;
      }

      const { assembly } = this.cadFile;
      if (assembly) {
        return assembly.GetProductById(prodId);
      }
    }
    return null;
  };

  findCadItemById = cadId => {
    const instance = this.cadItemByInstanceId(cadId);
    if (instance) {
      return instance;
    }
    const part = this.cadItemByPartId(cadId);
    if (part) {
      return part;
    }
    const product = this.cadItemByProdId(cadId);
    if (product) {
      return product;
    }
    return null;
  };

  findCadItemForNote = note => {
    const noteCameraData = note.GetCameraDataJsonObj();
    const forCadId = noteCameraData.cadId;
    return this.findCadItemById(forCadId);
  };

  findCadItemForMeasurement = measurement => {
    return this.findCadItemById(measurement.forCadId);
  };

  @action InspectById = cadId => {
    const item = this.findCadItemById(cadId);
    if (item) {
      this.InspectCadItem(item);
    } else {
      this.reportError(`Could not inspect cadId: ${cadId}`);
    }
  };

  @action InspectPartById = partId => {
    const instance = this.cadItemByPartId(partId);
    if (instance) {
      this.InspectCadItem(instance);
    }
  };

  @action InspectProductById = prodId => {
    const prod = this.cadItemByProdId(prodId);
    if (prod) {
      this.InspectCadItem(prod);
    }
  };

  getCameraForId = id => {
    let partId = id;
    let prodId = id;
    const splitId = id.split('-');
    if (splitId.length > 1) {
      [prodId, partId] = splitId;
    }

    const { settings } = this.viewer;
    const cameraCount = settings.CameraCount();
    for (let i = 0; i < cameraCount; i++) {
      const camera = settings.GetCamera(i);
      const itemId = camera.GetItem()?.uuid;
      if (itemId && (itemId === partId || itemId === prodId || itemId === id)) {
        return camera;
      }
    }
    return null;
  };

  // - inspecting a cad item means opening a tab for it and looking at it in
  // isolation
  // - if the tab is alrady open this switches to it
  // - we also update the route params to contain prodId= for subassemblies and
  // partId= for bodies
  @action InspectCadItem = cadItem => {
    // unselect
    this.UnHighlightEverything();
    const selfTarget = cadItem?.GetSelfTargetItem();
    if (selfTarget) {
      const selIx = this.viewer.selectionManager.ContainsSelectionItemTarget(
        selfTarget._wasm,
      );
      if (selIx >= 0) this.viewer.selectionManager.DeleteSelection(selIx);
    }
    this.SetHighlightBehaviour(null, null);

    const newParams = { ...routerStore.route.params };
    if (cadItem && cadItem.IsAssemblyInstance()) {
      // - inspect body
      const newpath = routerStore.route.name;
      delete newParams.prodId;
      delete newParams.partId;
      routerStore.navigate(
        newpath,
        {
          ...newParams,
          cadId: cadItem.uuid,
        },
        // - replace so that the history is replaced, we keep only the current
        // tab as the last history, so back button always go back to the file
        // list
        // - if we go back and then go forward, we end in the last looked at tab
        { replace: true },
      );
      // - if we can find a camera for the id, then it means we already have
      // a tab open for the thing we're about to inspect, this means we need
      // to switch to the opened tab here
      // - otherwise if the camera does not yet exist then something else adds
      // it for us and switches to it (no idea what currently)
      const camera = this.getCameraForId(cadItem.uuid);
      if (camera) {
        this.viewer.settings.SetCamera(camera.GetIndex());
      }
    } else if (cadItem && cadItem.IsAssemblyProduct()) {
      // - inspect subassembly
      const newpath = routerStore.route.name;
      delete newParams.cadId;
      delete newParams.partId;
      routerStore.navigate(
        newpath,
        {
          ...newParams,
          prodId: cadItem.uuid,
        },
        { replace: true },
      );
      const camera = this.getCameraForId(cadItem.uuid);
      if (camera) {
        this.viewer.settings.SetCamera(camera.GetIndex());
      }
    } else {
      // - inspecting nothing means removing the partId and prodId from the
      // route params and then switching to camera 0 (which is always there,
      // so no need to check for it)
      const newpath = routerStore.route.name;
      delete newParams.cadId;
      delete newParams.partId;
      delete newParams.prodId;
      routerStore.navigate(
        newpath,
        {
          ...newParams,
        },
        // - if I don't put reload: true here, the prodId/partId are not deleted
        // - reload does not actually reload, it just force a navigate even when
        // the url did not change (which it did though, router5 is just wrong)
        { reload: true },
      );
      this.viewer.settings.SetCamera(0);
    }

    if (cadItem) {
      this.viewer.settings.FocusItemCamera(cadItem._wasm);
    }
  };

  /*
    Camera
  */

  @action ResetCamera = () => {
    if (this.viewer) {
      this.viewer.settings.Reset();
      this.cadFile.assembly.RestoreVisibility();
      this.ForceRender();
    }
  };

  get currentInstance() {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (camera && camera.IsFacetedMesh()) {
      return camera.GetInstance();
    }
    return null;
  }

  @action CameraUpdated = () => {
    this.viewer?.notePad?.reportWasmChange();

    this.SectionViewUpdated();

    const camera = this.viewer?.settings?.CurrentCamera();
    camera?.reportWasmChange();
    // if (streamPatch === true) {
    //   // report changes to all collaborators...
    //   this.streamCameraPatch();
    // }
  };

  @action SectionViewUpdated = () => {
    this.sectionManager?.reportWasmChange();
  };

  @action ViewUpdated = streamPatch => {
    this.viewer?.navCube?.reportWasmChange();
    this.viewer?.settings?.reportWasmChange();
    this.CameraUpdated(streamPatch);
  };

  @action SelectionUpdated = () => {
    this.viewer?.selectionManager?.reportWasmChange();
    // if (streamPatch === true) {
    //   // report changes to all collaborators...
    //   this.streamSelectionPatch();
    // }
  };

  // "standard" orthogonal views
  @action SetOrthoView = newOrbitEnum => {
    const enumValue = newOrbitEnum ? newOrbitEnum.value : -1;
    const orbitEnum = this.viewer?.module.CameraOrbit;
    const orbitIx = this.viewer?.settings?.GetOrbit();
    if (orbitIx !== enumValue) {
      // value changed, apply it
      switch (newOrbitEnum) {
        case orbitEnum.OrthoTop:
          this.viewer?.settings?.SetOrthoTop();
          break;
        case orbitEnum.OrthoBottom:
          this.viewer?.settings?.SetOrthoBottom();
          break;
        case orbitEnum.OrthoFront:
          this.viewer?.settings?.SetOrthoFront();
          break;
        case orbitEnum.OrthoRear:
          this.viewer?.settings?.SetOrthoRear();
          break;
        case orbitEnum.OrthoLeft:
          this.viewer?.settings?.SetOrthoLeft();
          break;
        case orbitEnum.OrthoRight:
          this.viewer?.settings?.SetOrthoRight();
          break;
        default:
          break;
      }
    } else {
      // same value, clear it
      this.viewer?.settings?.ClearOrthoView(false);
    }
  };

  // camera orbit mode
  @action SetCameraOrbit(cameraEnum) {
    const enumValue = cameraEnum ? cameraEnum.value : -1;
    const orbitEnum = this.viewer?.module.CameraOrbit;
    const orbitIx = this.viewer?.settings?.GetOrbit();
    if (orbitIx !== enumValue) {
      // value changed, apply it
      switch (cameraEnum) {
        case orbitEnum.FixedUp:
          this.viewer?.settings.SetOrbitFixedUp();
          break;
        case orbitEnum.Free:
          this.viewer?.settings.SetOrbitFree();
          break;
        case orbitEnum.FixedX:
          this.viewer?.settings.SetOrbitFixedX();
          break;
        case orbitEnum.FixedY:
          this.viewer?.settings.SetOrbitFixedY();
          break;
        case orbitEnum.FixedZ:
          this.viewer?.settings.SetOrbitFixedZ();
          break;
        default:
          break;
      }
    } else {
      // same value, clear it
      this.viewer?.settings.ClearOrthoView(false);
    }
  }

  /*
    Mesh Analysis
     (depecated)
  */

  @computed get runningMeshAnalysis() {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (camera && camera.IsFacetedMesh()) {
      const runningField = this.viewer.settings.GetRunningTest();
      return runningField;
    }
    return null;
  }

  @computed get currentMeshAnalysis() {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (camera && camera.IsFacetedMesh()) {
      return camera.GetCurrFieldGradient();
    }
    return null;
  }

  @computed get currentMeshFrame() {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (camera && camera.IsFacetedMesh()) {
      const instance = camera.GetInstance();
      const mesh = instance?.GetBody()?.GetBodyMesh();
      if (mesh && (mesh.manifoldScore < 0 || mesh.isManifold)) {
        const frame = mesh.GetMeshFrame();
        if (frame && !frame.isNull) {
          return frame;
        }
      }
    }
    return null;
  }

  @action StartMeshTopologyReconstruction = () => {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (camera && camera.IsFacetedMesh()) {
      const instance = camera.GetInstance();
      const mesh = instance?.GetBody()?.GetBodyMesh();
      if (mesh && (mesh.manifoldScore < 0 || mesh.isManifold)) {
        const frame = mesh.GetMeshFrame();
        if (frame && !frame.topologyReady) {
          // launch mesh topology analysis
          const fieldModeEnum = this.viewer.module.TessFieldType;
          this.viewer.settings.CalculateFieldGradient(
            fieldModeEnum.TessFieldTopology,
            -1, // target frame rate, 1st time unknown
          );
        }
      }
    }
  };

  /*
    DFM Analysis
  */

  @action RequestDfmAnalysis = body => {
    if (body && body.isPersistent) {
      if (this.meshAnalysis.hasFinished) return;

      if (this.meshAnalysis.isWaiting) {
        // an analysis is already runing, for the same body?
        if (this.meshAnalysis.dfmBody?.uuid === body?.uuid) {
          return;
        }
      }

      // clear previous dfm selection (if any)
      this.viewer.selectionManager.ClearDfmSelection();
      // delete wasm observable
      this.obsMap.delete(body.GetDfm()?._mapKey);
      // request new analysis
      this.meshAnalysis.RequestMeshAnalysis(body);
    }
  };

  @action RequestCutoffAnalysis = (body, processId, units = 'mm') => {
    if (body?.isReadyForDfmCutoff) {
      const dfm = body?.GetDfm();

      // clear previous dfm selection
      this.viewer.selectionManager.ClearDfmSelection();

      // invalidate last cutoff analysis
      dfm.Clear();
      // delete wasm observable
      this.obsMap.delete(dfm._mapKey);
      this.meshAnalysis.lastCutoffAnalysis = new DfmCutoffAnalysis();

      if (processId) {
        // get cutoffs for the fabrication process
        const cutOffs = new DfmProcessCutoffs(processId, units);
        if (cutOffs && !cutOffs.isEmpty) {
          const cutoffStr = JSON.stringify(cutOffs);
          dfm.SetCutoffs(cutoffStr);
          dfm.Run();

          // wrap cutoff analysis result
          const dfmEnum = this.viewer.module.DfmAnalysisType;
          const newErrorList = new DfmCutoffAnalysis(
            processId,
            body.GetDfm(),
            dfmEnum,
          );
          this.meshAnalysis.lastCutoffAnalysis = newErrorList;
        } else {
          // no available cutoffs for this process yet...
          this.meshAnalysis.lastCutoffAnalysis = null;
        }
      }

      this.meshAnalysis.dfmStatus = DfmStatus.CUTOFF;
    }
  };

  hasDfmHighlights = group => {
    return this.viewer?.selectionManager?.IsModelItemHighlightTarget(group);
  };

  hasDfmSelections = group => {
    return this.viewer?.selectionManager?.IsModelItemSelectionTarget(group);
  };

  @action HighlightDfmWarnings = type => {
    this.viewer?.settings?.DrawDfmWarnings(type, false);
  };

  hasDfmSelections = group => {
    return this.viewer?.selectionManager?.IsModelItemSelectionTarget(group);
  };

  @action SelectDfmWarnings = type => {
    this.viewer?.settings?.DrawDfmWarnings(type, true);
  };

  // deprecated
  @computed get runningWarnings() {
    const currGradient = this.currentMeshAnalysis;
    if (currGradient) {
      return currGradient.isCalculatingWarnings;
    }
    return false;
  }

  @action StartWarningsCalculation = () => {
    if (this.runningWarnings) return;

    const fieldModeEnum = this.viewer.module.TessFieldType;
    if (this.runningMeshAnalysis !== fieldModeEnum.TessFieldNone) return;

    const currGradient = this.currentMeshAnalysis;
    if (
      currGradient &&
      (currGradient.type === fieldModeEnum.TessFieldOverHang ||
        currGradient.type === fieldModeEnum.TessFieldSharpEdges ||
        currGradient.type === fieldModeEnum.TessFieldThickness)
    ) {
      const warnings = currGradient.GetWarnings();
      if (!warnings) {
        // no warnings yet, calculate it...
        const instance = this.currentInstance;
        if (instance && instance.isFacetedBody) {
          const mesh = instance.GetFacetedBody();
          currGradient.CalculateCutOffWarnings(mesh._wasm, -1); // target frame rate, 1st time unknown (-1)
        }
      }
    }
  };

  @action WarningsUpdated = () => {
    const currGradient = this.currentMeshAnalysis;
    if (currGradient) {
      currGradient.reportWasmChange();

      // if (streamPatch) {
      //   // TODO
      // }
    }
  };

  // features

  @computed get currentDFMFeatures() {
    const frame = this.currentMeshFrame;
    if (frame && frame.featuresReady) {
      const featTypeEnum = this.viewer.module.TopoFeatureType;

      const featureMap = new Map();
      featureMap.set('Hole', []);
      featureMap.set('Engraved', []);
      featureMap.set('Embossed', []);

      // HOLES: sorted by extent/diameter (smaller frist)
      let features = frame.GetFeatures(featTypeEnum.TopoFeatureHole);
      let nType = features.size();
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < nType; i++) {
        const feat = features.get(i);
        featureMap.get('Hole').push(feat);
      }
      featureMap.get('Hole').sort((a, b) => {
        const extA = a.GetExtent();
        const extB = b.GetExtent();
        const maxA = extA.y < 0 ? extA.x : extA.y;
        const maxB = extB.y < 0 ? extB.x : extB.y;
        if (maxA === maxB) return 0;
        if (maxA > maxB) return 1; // a after b
        return -1; // b after a
      });

      // ENGRAVED: sorted by inwards span (smaller frist)
      features = frame.GetFeatures(featTypeEnum.TopoFeatureSlot);
      nType = features.size();
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < nType; i++) {
        const feat = features.get(i);
        featureMap.get('Engraved').push(feat);
      }
      featureMap.get('Engraved').sort((a, b) => {
        const spanA = a.span;
        const spanB = b.span;
        if (spanA === spanB) return 0;
        if (spanA > spanB) return 1; // a after b
        return -1; // b after a
      });

      // EMBOSSED: sorted by outwards span (smaller frist)
      features = frame.GetFeatures(featTypeEnum.TopoFeatureBoss);
      nType = features.size();
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < nType; i++) {
        const feat = features.get(i);
        featureMap.get('Embossed').push(feat);
      }
      featureMap.get('Embossed').sort((a, b) => {
        const spanA = a.span;
        const spanB = b.span;
        if (spanA === spanB) return 0;
        if (spanA > spanB) return 1; // a after b
        return -1; // b after a
      });

      return featureMap;
    }
    return null;
  }

  // DIFF
  get isDiffPanelVisible() {
    return this.viewer?.isDiffing && routerStore?.route.params.panel === 'diff';
  }

  get shouldShowCameraPanel() {
    return routerStore?.route.params.panel === 'camera';
  }

  get isDiffLoading() {
    return this.viewerVisible && this.isDiffRoute && this.viewer.isDiffLoading;
  }

  get isDiffRoute() {
    return routerStore?.route.params.diff !== undefined;
  }

  /*
    State streaming
  */

  // streamCameraPatch = () => {
  //   // report changes to all collaborators...
  //   const { session } = this.viewer;
  //   if (session && session.UserCount() > 0) {
  //     const mainCamera = this.viewer.settings.GetCamera(0);
  //     // eslint-disable-next-line no-console
  //     // console.log('STREAMING -CAMERA- PATCH');
  //     if (mainCamera) {
  //       // const patch = mainCamera.ToJson();
  //     }
  //   }
  // };

  // streamViewPatch = () => {
  //   // report changes to all collaborators...
  //   const { session } = this.viewer;
  //   if (session && session.UserCount() > 0) {
  //     const { settings } = this.viewer;
  //     // eslint-disable-next-line no-console
  //     // console.log('STREAMING -VIEW- PATCH');
  //     if (settings) {
  //       // const patch = settings.ToJson();
  //     }
  //   }
  // };

  // streamSelectionPatch = () => {
  //   // report changes to all collaborators...
  //   const { session } = this.viewer;
  //   if (session && session.UserCount() > 0) {
  //     const selManager = this.viewer.selectionManager;
  //     // eslint-disable-next-line no-console
  //     // console.log('STREAMING -SELECTION- PATCH');
  //     if (selManager) {
  //       // const patch = selManager.ToJson();
  //     }
  //   }
  // };

  /*
    UI
  */

  get defaultClipPlaneType() {
    const clipEnum = this?.viewer?.module.ClipPlaneType;
    return clipEnum.ClipPlaneBottom.value;
  }

  @action Escape = () => {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (!camera) return;

    const selManager = this.viewer?.selectionManager;
    const selModeLink = selManager?.IsSelectionModeLink();
    if (selModeLink) {
      this.ExitPinMode();
      return;
    }

    const selMode = selManager?.GetSelectionMode();
    const selModeEnum = this.wftyModule?.SelectionMode;
    if (selMode === selModeEnum.SelectionModeMeasurements) {
      const pmi = this.cadFile?.pmi;
      const temp = pmi.GetStartedMeasurement();
      if (temp != null) {
        // delete temp measurement
        pmi.AbortStartedMeasurement();
      } else {
        // exit measurement mode
        const lastSelMkp = selManager.GetLastMarkupSelection();
        if (lastSelMkp) {
          selManager.ClearSelection();
        }
        this.SyncSelectionMode();
      }
      return;
    }

    // clear selection
    selManager?.ClearSelection();
  };

  @action ExitPinMode = () => {
    const camera = this.viewer?.settings?.CurrentCamera();
    if (!camera) return false;

    const selManager = this.viewer?.selectionManager;
    const selModeLink = selManager?.IsSelectionModeLink();
    const selModeEnum = this.wftyModule?.SelectionMode;

    let somethingToggled = false;
    if (selModeLink) {
      // exit pin target selection mode...
      if (camera.IsInstance()) {
        selManager?.SetSelectionMode(selModeEnum.SelectionModeGeometry);
      } else {
        selManager?.SetSelectionMode(selModeEnum.SelectionModeAssembly);
      }
      somethingToggled = true;
    } else if (this.toolBarExpanded === 'pin') {
      // hide toolBar pin addtition menu
      this.toolBarExpanded = null;
      somethingToggled = true;
    } else if (selManager?.GetSelectionCount() > 0) {
      // clear selection...
      selManager?.ClearSelection();
      somethingToggled = true;
    }

    return somethingToggled;
  };

  @action StartMeasureMode = () => {
    const selManager = this.viewer?.selectionManager;
    const settings = this.viewer?.settings;
    const pmi = this.cadFile?.pmi;
    const selModeEnum = this.viewer?.module?.SelectionMode;
    const lastSelMkp = selManager.GetLastMarkupSelection();
    // If it's an assembly file, the 1st time it needs to parse the brep json
    //  before starting measurement mode, so may take some miliseconds...
    setTimeout(() => {
      selManager.SetSelectionMode(selModeEnum.SelectionModeMeasurements);
      // set default measurments visibility
      settings.measureVisible = true;
      // set default measurment mode
      if (!lastSelMkp) {
        pmi.SetFromToMode(true);
      }
      // hide toolbar options, floating window will appear
      this.toolBarExpanded = null;
    }, 10);
  };

  // TODO: alex - move logic of opening/closing Assembly Tree to uiStore
  getLeftOffset = () => {
    const camera = this.viewer?.settings?.CurrentCamera();

    if (camera?.IsDrawing()) {
      return 0;
    }

    if (this.uiMode !== UiModes.collapsed) {
      return UiPosition.panel;
    }
    return 0;
  };

  @action ToggleModalVisibility() {
    this.lightModalVisible = !this.lightModalVisible;
  }

  @action SetLightModalVisibility(value) {
    this.lightModalVisible = value;
  }

  @action ToggleExplodeMode = value => {
    if (!this.viewer) {
      return;
    }
    if (value !== undefined) {
      this.viewer.engine.SetExplodeRunning(value);
    } else if (this.viewer.engine.ExplodeRuning()) {
      // turning off
      this.viewer.engine.SetExplodeRunning(false);
    } else {
      // turning on
      this.viewer.engine.SetExplodeRunning(true);
    }
  };

  @action DisableSectionMode = () => {
    if (!this.viewer) {
      return;
    }
    // - the ClipPlanes are on a stack, if you disable the first one the
    // remaining ones will move up, so to disable all of the ClipPlanes
    // we need to disable the first one three times
    // - we could also do 3,2,1 or maybe even 1,2,1 but 1,1,1 makes the
    // most sense
    this.viewer?.sectionManager.SwitchOffClipSection(1);
    this.viewer?.sectionManager.SwitchOffClipSection(1);
    this.viewer?.sectionManager.SwitchOffClipSection(1);

    this.viewer.sectionManager.SetActive(false);
    this.UpdateUiMode(UiModes.collapsed);
  };

  @action DisableAnnotationMode = () => {
    this.TogglePinExpand();
    // hide pin addition menu
    this.toolBarExpanded = null;
    // exit addition mode
    this.SyncSelectionMode();
  };

  /*
    Debug / Log
    (output wasm logs into console)
  */

  getExceptionMessage = what => {
    // - "what" comes most likely from a wasm exception, so it
    // should be a string and not an error
    // - we need to pass an Error() to logFail (and sentry), so
    // when we get a string, then it is a wasm exception and we
    // get the actual exception from wasm with getExceptionMessage
    // (which takes a pointer value), and then make an Error()
    // out of that
    if (typeof what === 'string' || what instanceof String) {
      let matches = what.match(/^([0-9]+)$/);
      if (!matches || matches.length === 1) {
        matches = what.match(/unhandled exception: ([0-9]+),/);
      }
      if (matches?.length > 1) {
        const ptr = parseInt(matches[1], 10);
        if (Number.isSafeInteger(ptr)) {
          const msg = this.wftyModule.getExceptionMessage(ptr);
          return msg;
        }
      }
    } else if (Number.isSafeInteger(what)) {
      const msg = this.wftyModule.getExceptionMessage(what);
      return msg;
    }

    return undefined;
  };

  @action LogFail = (what, scopingFunction = () => {}) => {
    this.viewerFailed = true;

    const localScopingFunction = scope => {
      scope.setTag('subsystem', 'wasm-fail');
      scope.setTag('exception', what);
      scopingFunction(scope);
    };

    const msg = this.getExceptionMessage(what);
    let err = what;
    if (msg) {
      err = new LogFailError(msg);
    }
    this.reportError(err, localScopingFunction);
  };

  @action LogConversionProblem = (what, scopingFunction = () => {}) => {
    this.conversionProblem = true;

    const localScopingFunction = scope => {
      scope.setTag('subsystem', 'wasm-conversion-problem');
      scope.setTag('exception', what);
      scopingFunction(scope);
    };

    const msg = this.getExceptionMessage(what);
    let err = what;
    if (msg) {
      err = new ConversionProblemError(msg);
    }
    this.reportError(err, localScopingFunction);
  };

  @action LogException = (what, scopingFunction = () => {}) => {
    const localScopingFunction = scope => {
      scope.setTag('subsystem', 'wasm-exception');
      scope.setTag('exception', what);
      scopingFunction(scope);
    };

    const msg = this.getExceptionMessage(what);
    let err = what;
    if (msg) {
      err = new GltfViewerException(msg);
    }

    if (err instanceof GltfViewerException) {
      if (!this.reportedExceptions.get(err?.toString())) {
        // - if this is a GltfViewerException, and we have not seen it before,
        // just report it and continue
        this.reportedExceptions.set(err?.toString(), true);
        this.reportError(err, localScopingFunction);
        return;
      }

      if (this.reportedExceptions.get(err?.toString())) {
        // - if this is a GltfViewerException that we have seen before, then
        // report it again but also set the viewer to failed
        this.reportError(err, localScopingFunction);
        this.viewerFailed = true;
        return;
      }
    }

    // - if this is an Error, or just a string, report it
    this.reportError(err, localScopingFunction);
  };

  reportError = (what, scopingFunction = () => {}) => {
    let err = what;
    if (!(err instanceof Error)) {
      // - we pass strings as Errors, so make sure to convert those to Error
      // before we pass them to analytics.logError
      err = new Error(what);
    }
    const localScopingFunction = scope => {
      scope.setExtra('heap8Size', this.heap8SizeMb());
      scopingFunction(scope);
    };
    analytics.logError(err, localScopingFunction);
  };

  logWarn = what => {
    analytics.addBreadcrumb({
      category: 'gltf-viewer',
      message: what,
      level: 'warn',
    });
    // eslint-disable-next-line no-console
    console.warn(what);
  };

  logInfo = what => {
    analytics.addBreadcrumb({
      category: 'gltf-viewer',
      message: what,
      level: 'info',
    });
    // eslint-disable-next-line no-console
    console.log(what);
  };

  logDebug = what => {
    // eslint-disable-next-line no-console
    console.log(what);
  };

  heap8SizeMb = () => {
    if (this.viewer && this.viewer.module) {
      const heap8SizeMb = this.viewer.module.HEAP8.length / 1000000;
      return heap8SizeMb;
    }
    return -1;
  };

  /*
    debug
  */

  // fakeAddCollaborator = () => {
  //   const settings = this.viewer?.settings;
  //   const session = this.viewer?.session;
  //   if (settings && session) {
  //     const camera = settings.CurrentCamera();
  //     if (camera) {
  //       // get fake camera data
  //       const cameraJsonStr = settings.GetCameraPatch(camera.GetIndex());
  //       const userId = `fakeUser${new Date().getTime()}`;
  //       // add fake collaborator...
  //       session.SetUser(userId, cameraJsonStr);
  //       const userCount = session.UserCount();
  //       // eslint-disable-next-line no-console
  //       console.log(
  //         'fake collaborator added:',
  //         userId,
  //         cameraJsonStr,
  //         '(',
  //         userCount,
  //         'users)',
  //       );
  //     }
  //   }
  // };

  // fakeRedirect = () => {
  //   const rfqId = 'UkZROjB4Y2Q0M2Mw';
  //   clientStore.routerStore.router.navigate('rfq', { rfqId });
  // };

  // fakeWebGLctxLost = () => {
  //   if (!this.viewer) return;
  //   if (!document) return;
  //   const canvasHolder = document.getElementById(this.canvasId);
  //   if (!canvasHolder) return;

  //   let gl = canvasHolder.getContext('webgl2');
  //   if (gl == null) {
  //     gl = canvasHolder.getContext('webgl1');
  //     if (gl == null) {
  //       gl = canvasHolder.getContext('webgl');
  //       if (gl == null) {
  //         console.error('cannot get webGL context');
  //         return;
  //       }
  //     }
  //   }

  //   const ext = gl.getExtension('WEBGL_lose_context');
  //   const restoreSeconds = 3;

  //   if (!gl.isContextLost()) {
  //     // eslint-disable-next-line no-console
  //     console.log(
  //       'forcing [WEBGL_lose_context]. Restoring it in',
  //       restoreSeconds,
  //       'seconds...',
  //     );
  //     // force lost...
  //     ext.loseContext();

  //     // force restore...
  //     setTimeout(() => {
  //       ext.restoreContext();
  //     }, 1000 * restoreSeconds);
  //   }
  // };

  // // DiffTool
  // // TEST: for a given file, if it belongs to a project named "diff",
  // //  requests it's counterpart diffing file.
  // getFakeDiffFile = () => {
  //   if (!this.cadReady) return null;
  //   if (!this.file) return null;

  //   const slug = routerStore.params.get('slug');

  //   const spaceType = clientStore.spaceRoute.route;
  //   const parentSpace = spaceType.split('.')[0];
  //   const space = routerStore.params.get(parentSpace);

  //   if (space && (slug === 'diff' || slug === 'assydiff')) {
  //     const { filename } = this.file;
  //     const nameArr = filename.split('.');
  //     let filepath;
  //     if (nameArr[0].includes('_edited')) {
  //       filepath = `${nameArr[0].replace('_edited', '')}.${nameArr[1]}`;
  //     } else {
  //       filepath = `${nameArr[0]}_edited.${nameArr[1]}`;
  //     }

  //     return {
  //       space,
  //       slug,
  //       filepath,
  //       version: undefined,
  //     };
  //   }
  //   return null;
  // };
}
